import { supabase } from '../lib/supabase';
import { subscriptionService } from '../services/subscription';

export async function checkSubscriptionStatus() {
  try {
    const { data: { user }, error } = await supabase.auth.getUser();
    if (error) throw error;

    if (!user?.email) return;

    const status = await subscriptionService.checkSubscriptionStatus(user.email);
    
    // If expired, handle expiration
    if (status.status === 'expired') {
      // Send email notification using localStorage to avoid multiple notifications
      const lastNotification = localStorage.getItem('lastDowngradeNotification');
      if ((!lastNotification || Date.now() - parseInt(lastNotification) > 86400000)) {
        await sendDowngradeEmail(user.email);
        localStorage.setItem('lastDowngradeNotification', Date.now().toString());
      }
    }
    // If approaching expiration (7 days before)
    else if (status.daysLeft <= 7 && status.status === 'premium') {
      // Send reminder email
      const lastReminder = localStorage.getItem('lastExpirationReminder');
      if ((!lastReminder || Date.now() - parseInt(lastReminder) > 86400000)) {
        const expiryDate = new Date();
        expiryDate.setDate(expiryDate.getDate() + status.daysLeft);
        await sendReminderEmail(user.email, expiryDate);
        localStorage.setItem('lastExpirationReminder', Date.now().toString());
      }
    }

    // Add this call to ensure local data is in sync
    await refreshSubscriptionData();
  } catch (error) {
    console.error('Error checking subscription status:', error);
  }
}

// Simple email sending function using mailto: protocol
async function sendReminderEmail(email: string, expiryDate: Date) {
  const subject = 'Your subscription is expiring soon';
  const body = `Your premium subscription will expire on ${expiryDate.toLocaleDateString()}. Please renew to maintain access to premium features.`;
  window.open(`mailto:${email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`);
}

async function sendDowngradeEmail(email: string) {
  const subject = 'Subscription Expired';
  const body = 'Your premium subscription has expired. Your account has been downgraded to trial mode.';
  window.open(`mailto:${email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`);
}

// Add a function to refresh subscription data from server to local
export async function refreshSubscriptionData() {
  try {
    const { data: { user }, error } = await supabase.auth.getUser();
    if (error || !user?.email) return;

    // Force refresh from server
    const status = await subscriptionService.checkSubscriptionStatus(user.email);
    
    // If expired, update localStorage as well
    if (status.status === 'expired') {
      try {
        const storedUsers = localStorage.getItem('premium_users');
        if (storedUsers) {
          const users = JSON.parse(storedUsers);
          if (users[user.email]) {
            users[user.email].status = 'expired';
            localStorage.setItem('premium_users', JSON.stringify(users));
          }
        }
      } catch (e) {
        console.error('Error updating localStorage:', e);
      }
    }
  } catch (error) {
    console.error('Error refreshing subscription data:', error);
  }
}
